import React, { useCallback, useEffect } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Skeleton } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { CopyToClipboard } from 'src/Elements/CopyToClipboard'
import { useAmazonCodeDataById } from '../hooks'
import { AMAZON_CODE_ID_QUERY_PARAM } from '../const'

export interface AmazonCodeDialogProps {
  amazonCodeId: string
  removeAmazonCodeToken: () => void
  onClose?: () => void
}

const useStyles = makeStyles()(theme => ({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    paddingBlock: theme.spacing(1),
  },
  skeleton: {
    height: theme.spacing(6),
  },
}))

/**
 * Dialog which shows an amazon code from email
 */
const AmazonCodeDialog: React.FC<AmazonCodeDialogProps> = ({ amazonCodeId, removeAmazonCodeToken, onClose }) => {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useAmazonCodeDataById(amazonCodeId)
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { enqueueErrorStack } = useSnackbar()

  const serialNumber = data?.serial ?? ''
  const amazonCode = data?.code ?? ''

  const onCloseHandler = useCallback(() => {
    removeAmazonCodeToken()
    const queryParams = new URLSearchParams(window.location.search)

    queryParams.delete(AMAZON_CODE_ID_QUERY_PARAM)
    navigate({ search: queryParams.toString() })
    onClose?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, onClose])

  useEffect(() => {
    if (isError) {
      onCloseHandler()
      removeAmazonCodeToken()
      enqueueErrorStack(t('referal-codes.amazon-code-dialog.error'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, t])

  if (isError) {
    return null
  }

  return (
    <Dialog title={t('referal-codes.amazon-code-dialog.title')} maxWidth="sm" onClose={onCloseHandler}>
      <Box className={classes.mainContainer}>
        {isLoading ? (
          <>
            <Skeleton className={classes.skeleton} variant="rounded" />
            <Skeleton className={classes.skeleton} variant="rounded" />
          </>
        ) : (
          <>
            <CopyToClipboard label={t('referal-codes.amazon-code-dialog.gift-card-code')} value={amazonCode} />
            <CopyToClipboard label={t('referal-codes.amazon-code-dialog.serial-number')} value={serialNumber} />
          </>
        )}
      </Box>
    </Dialog>
  )
}

export default AmazonCodeDialog
